import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import truncate from 'lodash/truncate';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { CardBody, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import CountdownTimer from 'Components/Utility/CountdownTimer';
import Drm from 'Components/Utility/Drm';
import FormatProductName from 'Helpers/Format/FormatProductName';
import HitCardViewBtn from 'Components/Cards/HitCardViewBtn';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import ProductCover from 'Components/Utility/ProductCover';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ScreenshotCarousel from 'Components/Cards/ScreenshotCarousel';
import StarDealLabel from 'Components/StarDeal/StarDealLabel';

import 'Components/Cards/StarDealCard.scss';

const StarDealCard = ({
  starDeal,
  horizontal,
  lightTheme,
  starDealCardNameTag,
  bundle,
  intl,
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseHover = () => {
    setIsHovering(!isHovering);
  };

  let longAbout = '';
  let shortAbout = '';
  let shorterAbout = '';
  const operatingSystems = starDeal.operating_systems || [];

  // Check length of timer message to know which country
  const { formatMessage } = intl;

  const messages = defineMessages({
    ends: {
      id: 'ending',
      defaultMessage: 'Ends in 00 days or until sold out',
    },
  });

  if (isEmpty(starDeal)) return false;

  const longTimerText = formatMessage(messages.ends).length >= 44;

  let validDrm = '';
  let filteredDrm = [];
  if (starDeal && starDeal.drm) filteredDrm = starDeal.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const {
    slug,
    type,
    product_id: id,
    img,
    presale,
    link,
    hide_discount: hideDiscount,
    discount_percent: discountPercent,
    tiered,
    cover,
    sold_out: soldout,
    pay_what_you_want: payWhatYouWant,
    display_type: displayType,
    cover_type: coverType,
    game_total: gameTotal,
    sdescs,
    best_ever: bestEver,
    fullPrice,
    price,
    keysAvailablePercentage,
    age_ratings: ageRatings,
  } = starDeal;

  const name = FormatProductName(starDeal.name, type);
  let showAgeGate = ProductRequirements.showAgeGate(ageRatings);

  if (type === 'bundle') showAgeGate = false;

  const styling = {
    keys: {
      width: `${keysAvailablePercentage}%`,
    },
  };

  const TagName = starDealCardNameTag;

  const about = sdescs[window.locale];

  if (about && !longTimerText) {
    longAbout = truncate(about, { length: 200, separator: ' ' });
    shortAbout = truncate(about, { length: 140, separator: ' ' });
    shorterAbout = truncate(about, { length: 80, separator: ' ' });
  }

  if (about && longTimerText) {
    longAbout = truncate(about, { length: 200, separator: ' ' });
    shortAbout = truncate(about, { length: 100, separator: ' ' });
    shorterAbout = truncate(about, { length: 80, separator: ' ' });
    if (window.locale === 'ru') {
      shorterAbout = truncate(about, { length: 60, separator: ' ' });
    }
  }

  return (
    <article
      className={classNames('featured-deal-card faux-block-link', { horizontal }, { 'card-light-theme': lightTheme })}
      onMouseEnter={handleMouseHover}
      onMouseLeave={handleMouseHover}
    >
      {!horizontal &&
        <CardBody className="d-flex hit-card-overlay">
          <div className="overlayContent">
            <HitCardWishlistBtn productId={id} slug={slug} hit={starDeal} componentType="StarDealCard" />
            {!link &&
              <Link to={`/${type}/${slug}`} className="faux-block-link__overlay-link">
                <TagName className="product-name">{name}</TagName>
              </Link>
            }
            {link &&
              <Link to={link} className="faux-block-link__overlay-link">
                <TagName className="product-name">{name}</TagName>
              </Link>
            }

            <div className="featured-slider-container">
              {isHovering &&
                <React.Fragment>
                  {(isEmpty(img) || showAgeGate) &&
                    <ProductCover
                      hitcard
                      cover={cover}
                      alt={`Product cover for ${name}`}
                      displayType={displayType}
                      coverType={coverType}
                      sizesOverride="(min-width: 1520px) 420px, (min-width: 1200px) 360px, (min-width: 992px) 300px, (min-width: 768px) 220px, (min-width: 576px) 250px, 95vw"
                    />
                  }

                  {!isEmpty(img) && !showAgeGate &&
                    <Link to={HitLinkTo(starDeal)} className="faux-block-link__overlay-link">
                      <ScreenshotCarousel
                        screenshots={starDeal.screenshots}
                        type={type}
                        displayType={displayType}
                        gameTotal={gameTotal}
                        cardSize="medium"
                        isHovering={isHovering}
                      />
                    </Link>
                  }
                </React.Fragment>
              }
            </div>

            <div>
              <Row className="d-flex justify-content-center mt-2 mb-1 btn-container">
                <HitCardViewBtn hit={starDeal} />
                {!hideDiscount && !tiered && !presale && !payWhatYouWant &&
                  <AddToCartBtn showInCartLink id={id} slug={slug} />
                }
              </Row>
            </div>
          </div>
        </CardBody>
      }

      {isHovering && horizontal &&
        <CardBody className="d-flex hit-card-overlay">
          <div className="overlayContent">
            {(isEmpty(img) || showAgeGate) &&
              <div className="featured-slider-container">
                <ProductCover
                  hitcard
                  cover={cover}
                  alt={`Product cover for ${name}`}
                  displayType={displayType}
                  coverType={coverType}
                />
              </div>
            }
            {!isEmpty(img) && !showAgeGate &&
              <Link to={HitLinkTo(starDeal)} className="faux-block-link__overlay-link">
                <ScreenshotCarousel
                  screenshots={starDeal.screenshots}
                  type={type}
                  displayType={displayType}
                  gameTotal={gameTotal}
                  cardSize="medium"
                  isHovering={isHovering}
                />
              </Link>
            }

            <div className="details-container">
              <HitCardWishlistBtn productId={id} slug={slug} hit={starDeal} />

              {!link &&
                <Link to={`/${type}/${slug}`} className="faux-block-link__overlay-link">
                  <h6>{name}</h6>
                </Link>
              }
              {link &&
                <Link to={link} className="faux-block-link__overlay-link">
                  <h6>{name}</h6>
                </Link>
              }
              <div>
                <Row className="d-flex justify-content-center mt-2 mb-1 btn-container">
                  <HitCardViewBtn hit={starDeal} />

                  {!hideDiscount && !tiered && !presale && !payWhatYouWant &&
                    <AddToCartBtn
                      showInCartLink
                      id={id}
                      slug={slug}
                    />
                  }
                </Row>
              </div>

              <div className="card-strip">
                <div className="card-os-icons">
                  {operatingSystems.includes('windows') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaWindows} size="lg" className="mr-2" />
                    </span>
                  }
                  {operatingSystems.includes('mac') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaApple} size="lg" className="mr-2" />
                    </span>
                  }
                  {operatingSystems.includes('linux') &&
                    <span>
                      <FontAwesomeIcon icon={fabFaLinux} size="lg" />
                    </span>
                  }
                </div>
                {validDrm &&
                  <Drm drmType={validDrm} colorOverride={lightTheme ? 'dark' : ''} />
                }
                {!soldout &&
                  <div className="price-container">
                    <PriceContainer
                      price={price}
                      fullPrice={fullPrice}
                      discountPercent={discountPercent}
                      tiered={tiered}
                      hideDiscount={hideDiscount}
                      slug={slug}
                      payWhatYouWant={payWhatYouWant}
                    />
                  </div>
                }
                {soldout &&
                  <div className="soldout">
                    <FormattedMessage id="home.stardeal.soldout" defaultMessage="Sold Out" />
                  </div>
                }
              </div>
            </div>
          </div>
        </CardBody>
      }

      <CardBody key={id} className="card-main-container p-0">
        {starDeal &&
          <StarDealLabel />
        }

        <ProductCover
          hitcard
          cover={cover}
          alt={`Product cover for ${name}`}
          displayType={displayType}
          coverType={coverType}
        />

        <div className="w-100">
          <div className={`content-container ${longTimerText ? 'double-spaced-text-top' : ''}`}>
            <div className="description-section">
              {horizontal &&
                <div className="horizontal-name">
                  <TagName className="product-name">{name}</TagName>
                </div>
              }
              <div className={`${longTimerText ? 'about-container-small' : 'about-container'}  d-none d-xxl-block`}>
                {longAbout}
              </div>
              <div className={`${longTimerText ? 'about-container-small' : 'about-container'} d-none d-xl-block d-xxl-none`}>
                {shortAbout}
              </div>
              <div className={`${longTimerText ? 'about-container-small' : 'about-container'} d-none d-md-block d-xl-none`}>
                {shorterAbout}
              </div>
            </div>
            <div className={`details-section ${longTimerText ? 'double-spaced-text' : ''}`}>
              {!bundle &&
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated star-deal"
                    role="progressbar"
                    style={!soldout ? styling.keys : { width: '0%' }}
                    aria-valuenow={!soldout ? keysAvailablePercentage : 0}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Keys remaining"
                    title="Keys remaining"
                  />
                  {keysAvailablePercentage >= 85 &&
                    <div className="progress-timer progress-timer-overlay">
                      {keysAvailablePercentage}% &nbsp;<FormattedMessage id="home.stardeal.remaininghigh" defaultMessage="Remaining" tagName="span" />
                    </div>
                  }
                  {keysAvailablePercentage < 85 && keysAvailablePercentage >= 20 &&
                    <div className="progress-timer progress-timer-overlay">
                      {keysAvailablePercentage}% &nbsp;<FormattedMessage id="home.stardeal.remaining" defaultMessage="keys left" tagName="span" />
                    </div>
                  }
                  {keysAvailablePercentage < 20 &&
                    <div className="progress-timer progress-timer-overlay">
                      <FormattedMessage id="home.stardeal.remaininglow" defaultMessage="Last Few Keys Remaining" tagName="span" />
                    </div>
                  }
                </div>
              }
              {!soldout &&
                <div className="stardeal-ends">
                  <CountdownTimer
                    noLabel
                    featuredDeal
                    value={starDeal.available_valid_until * 1000}
                  />
                </div>
              }

              <div className="card-strip">
                <div className="os-drm-icons-container">
                  <div className="card-os-icons">
                    {operatingSystems.includes('windows') &&
                      <span>
                        <FontAwesomeIcon icon={fabFaWindows} size="lg" className="mr-2" />
                      </span>
                    }
                    {operatingSystems.includes('mac') &&
                      <span>
                        <FontAwesomeIcon icon={fabFaApple} size="lg" className="mr-2" />
                      </span>
                    }
                    {operatingSystems.includes('linux') &&
                      <span>
                        <FontAwesomeIcon icon={fabFaLinux} size="lg" />
                      </span>
                    }
                  </div>
                  {validDrm &&
                    <Drm drmType={validDrm} colorOverride={lightTheme ? 'dark' : ''} />
                  }
                </div>
                {!soldout &&
                  <div className="price-container">
                    {bestEver &&
                      <div className="best-ever">
                        <FormattedMessage id="home.stardeal.bestever" defaultMessage="Best Ever Price" />
                      </div>
                    }

                    <PriceContainer
                      price={price}
                      fullPrice={fullPrice}
                      discountPercent={discountPercent}
                      tiered={tiered}
                      hideDiscount={hideDiscount}
                      slug={slug}
                      payWhatYouWant={payWhatYouWant}
                    />
                  </div>
                }
                {soldout &&
                  <div className="soldout">
                    <FormattedMessage id="home.stardeal.soldout" defaultMessage="Sold Out" />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </article>
  );
};

StarDealCard.propTypes = {
  starDeal: PropTypes.shape({}),
  horizontal: PropTypes.bool,
  lightTheme: PropTypes.bool,
  starDealCardNameTag: PropTypes.string,
};

StarDealCard.defaultProps = {
  starDeal: {
    product: {
      product_id: '',
      cover: 'default.jpg',
      type: 'game',
      slug: '',
      name: '',
      sdesc: '',
      price: {},
      tiered: false,
      hide_discount: false,
      drm: [],
      link: '',
      presale: false,
    },
  },
  ageRatingBoard: 'PEGI',
  horizontal: false,
  lightTheme: false,
  starDealCardNameTag: 'h3',
};

export default connect(null)(injectIntl(StarDealCard));
